/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @author Satyaki Bose
 * @file SettingsSelectField.jsx
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  Select,
  Typography
} from '@mui/material';
import SettingsSelectDropdownIcon from './SettingsSelectDropdownIcon';
import { renderSettingsSelectMenuItem } from './SettingsSelectMenuItem';
import {
  SettingsSelectFieldStylesRoot,
  classes,
  getSettingsSelectMenuProps
} from './styles/settingsSelectFieldStyles';

const SettingsSelectField = ({
  label,
  value,
  options,
  disabled,
  className,
  config,
  onChange
}) => {
  const showSelectedCheckIcon = config?.showSelectedCheckIcon !== false;

  const selectClassName = [
    classes.selectRoot,
    disabled ? classes.selectDisabled : ''
  ].filter(Boolean).join(' ');

  return (
    <SettingsSelectFieldStylesRoot className={`${classes.wrapper} ${className || ''}`.trim()}>
      {label ? (
        <Typography component="label" className={classes.label}>
          {label}
        </Typography>
      ) : null}
      <FormControl variant="outlined" fullWidth disabled={disabled}>
        <Select
          value={value || ''}
          onChange={(event) => onChange?.(event.target.value)}
          renderValue={(selected) => (
            options.find((option) => option.value === selected)?.label || selected
          )}
          className={selectClassName}
          IconComponent={SettingsSelectDropdownIcon}
          MenuProps={getSettingsSelectMenuProps(classes.menuPaper, {
            PaperProps: {
              component: SettingsSelectFieldStylesRoot
            }
          })}
        >
          {options.map((option) => renderSettingsSelectMenuItem({
            option,
            selectedValue: value,
            classes,
            showSelectedCheckIcon
          }))}
        </Select>
      </FormControl>
    </SettingsSelectFieldStylesRoot>
  );
};

SettingsSelectField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  config: PropTypes.shape({
    showSelectedCheckIcon: PropTypes.bool
  }),
  onChange: PropTypes.func
};

SettingsSelectField.defaultProps = {
  label: '',
  value: '',
  options: [],
  disabled: false,
  className: '',
  config: {},
  onChange: null
};

export default SettingsSelectField;
